import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Zap, Trophy, Award, ArrowUp, Star, Target } from 'lucide-react';

const ANIM_DURATION = 3500;

const XPToast = ({ event, onDismiss }) => {
  const [phase, setPhase] = useState('enter');
  const timerRef = useRef(null);

  useEffect(() => {
    requestAnimationFrame(() => setPhase('visible'));
    timerRef.current = setTimeout(() => {
      setPhase('exit');
      setTimeout(onDismiss, 400);
    }, ANIM_DURATION);
    return () => clearTimeout(timerRef.current);
  }, [onDismiss]);

  if (!event) return null;

  const { xpAwarded, coinsEarned, questsCompleted, badgesEarned, level, eventType } = event;

  return (
    <div
      className={`pointer-events-auto transition-all duration-400 ease-out ${
        phase === 'enter' ? 'translate-x-full opacity-0 scale-90' :
        phase === 'visible' ? 'translate-x-0 opacity-100 scale-100' :
        'translate-x-full opacity-0 scale-90'
      }`}
      data-testid="xp-toast"
    >
      <div className="relative overflow-hidden rounded-xl border border-amber-500/30 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 shadow-2xl shadow-amber-500/20 p-4 min-w-[280px] max-w-[360px]">
        {/* Shimmer effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-500/5 to-transparent animate-shimmer" />
        
        {/* XP Award - Main */}
        {xpAwarded > 0 && (
          <div className="flex items-center gap-3 relative z-10">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center animate-pulse-slow">
              <Zap className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl font-black text-amber-400 tabular-nums animate-count-up">
                  +{xpAwarded}
                </span>
                <span className="text-xs font-semibold text-amber-400/70 uppercase tracking-wider">XP</span>
              </div>
              <p className="text-xs text-gray-400 truncate">
                {EVENT_LABELS[eventType] || 'Activity completed'}
              </p>
            </div>
            {coinsEarned > 0 && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-yellow-500/10 border border-yellow-500/20">
                <Star className="w-3.5 h-3.5 text-yellow-400" />
                <span className="text-sm font-bold text-yellow-400">+{coinsEarned}</span>
              </div>
            )}
          </div>
        )}

        {/* Quest Completed */}
        {questsCompleted?.length > 0 && (
          <div className={`flex items-center gap-2.5 relative z-10 ${xpAwarded > 0 ? 'mt-2.5 pt-2.5 border-t border-gray-700/50' : ''}`}>
            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Target className="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-emerald-400">Quest Complete!</p>
              <p className="text-xs text-gray-400 truncate max-w-[200px]">{questsCompleted[0]}</p>
            </div>
          </div>
        )}

        {/* Badge Earned */}
        {badgesEarned?.length > 0 && (
          <div className={`flex items-center gap-2.5 relative z-10 ${(xpAwarded > 0 || questsCompleted?.length) ? 'mt-2.5 pt-2.5 border-t border-gray-700/50' : ''}`}>
            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Award className="w-4 h-4 text-purple-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-purple-400">Badge Earned!</p>
              <p className="text-xs text-gray-400 truncate max-w-[200px]">
                {badgesEarned[0].name} ({badgesEarned[0].tier})
              </p>
            </div>
          </div>
        )}

        {/* Level indicator */}
        {level > 0 && (
          <div className="absolute top-2 right-2 flex items-center gap-1 text-[10px] text-gray-500 font-medium">
            <ArrowUp className="w-3 h-3" />Lv.{level}
          </div>
        )}
      </div>
    </div>
  );
};

const EVENT_LABELS = {
  video_complete: 'Video watched',
  reading_complete: 'Reading completed',
  quiz_submit: 'Quiz submitted',
  assignment_submit: 'Assignment submitted',
  lab_complete: 'Lab completed',
  topic_complete: 'Topic completed',
  course_complete: 'Course completed',
  daily_login: 'Daily login bonus',
};

export const GamificationToastContainer = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((event) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, event }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Expose addToast globally so NotificationContext can call it
  useEffect(() => {
    window.__gamToast = addToast;
    return () => { window.__gamToast = null; };
  }, [addToast]);

  return (
    <div
      className="fixed top-20 right-4 z-[9999] flex flex-col gap-3 pointer-events-none"
      data-testid="xp-toast-container"
    >
      {toasts.map(t => (
        <XPToast key={t.id} event={t.event} onDismiss={() => removeToast(t.id)} />
      ))}
    </div>
  );
};

export default GamificationToastContainer;
